import React, { useRef, useState } from 'react';
import './AttachmentPicker.scss';
import { Trans, useTranslation } from 'react-i18next';
import { CloseIcon } from '../common/CloseIcon';
import i18n from '../../i18n';

export interface AttachmentFile {
  id: string;
  file: File;
  preview?: string;
  uploading?: boolean;
  uploadProgress?: number;
  error?: string;
  uploaded?: boolean;
  attachmentData?: any;
}

interface AttachmentPickerProps {
  attachments: AttachmentFile[];
  onAddAttachments: (files: File[]) => void;
  onRemoveAttachment: (id: string) => void;
  disabled?: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
}

/**
 * Maps a server attachment refusal to a translation key. The backend sends English text (plus the
 * TOO_MANY_ATTACHMENTS code), not error codes, so only these refusals get a reason; callers fall back to a
 * generic message instead of printing a raw i18n key (JAA-433).
 */
export const getAttachmentErrorKey = (error?: string) => {
  if (!error) return undefined;
  if (error === 'TOO_MANY_ATTACHMENTS')
    return 'messages.error.TOO_MANY_ATTACHMENTS';
  if (error === 'Invalid image file' || error.startsWith('File type '))
    return 'messages.error.unsupportedFile';
  return undefined;
};

/**
 * Chat attachment tray: previews queued files and shows why the send was refused instead of a raw i18n key.
 */
const AttachmentPicker: React.FC<AttachmentPickerProps> = ({
  attachments,
  onAddAttachments,
  onRemoveAttachment,
  disabled,
  fileInputRef
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const { t } = useTranslation();

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      onAddAttachments(files);
    }
    // Reset input to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      onAddAttachments(files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (file: File): string => {
    const type = file.type;
    if (type.startsWith('image/')) return '🖼️';
    if (type.startsWith('video/')) return '🎥';
    if (type === 'application/pdf') return '📄';
    return '📎';
  };

  return (
    <div className="attachment-picker">
      {attachments.length > 0 && (
        <div className="attachment-picker__preview-list">
          {attachments.map((attachment) => (
            <div key={attachment.id} className="attachment-picker__preview-item">
              <div className="attachment-picker__preview-content">
                {attachment.preview ? (
                  <img 
                    src={attachment.preview} 
                    alt={attachment.file.name}
                    className="attachment-picker__preview-image"
                  />
                ) : (
                  <div className="attachment-picker__preview-icon">
                    {getFileIcon(attachment.file)}
                  </div>
                )}
                
                <div className="attachment-picker__preview-info">
                  <div className="attachment-picker__preview-name">
                    {attachment.file.name}
                  </div>
                  <div className="attachment-picker__preview-size">
                    {formatFileSize(attachment.file.size)}
                  </div>
                </div>

                {attachment.uploading && (
                  <div className="attachment-picker__progress">
                    <div 
                      className="attachment-picker__progress-bar"
                      style={{ width: `${attachment.uploadProgress || 0}%` }}
                    />
                  </div>
                )}

                {attachment.error && (
                  <div className="attachment-picker__error">
                    {t(
                      getAttachmentErrorKey(attachment.error) ??
                        'building.offMarket.modal.uploadFailed',
                    )}
                  </div>
                )}

                {!attachment.uploading && !disabled && (
                  <button
                    className="attachment-picker__remove"
                    onClick={() => onRemoveAttachment(attachment.id)}
                    aria-label="Remove attachment"
                  >
                    <CloseIcon size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div 
        className={`attachment-picker__dropzone ${isDragging ? 'attachment-picker__dropzone--dragging' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/*,application/pdf"
          onChange={handleFileSelect}
          disabled={disabled}
          style={{ display: 'none' }}
        />
      </div>
    </div>
  );
};

export default AttachmentPicker;


